import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { COLLECTION_SCHEMAS } from "@/admin/collectionConfigs";
import { Field, TextInput, TextArea, Select, ImageInput, Toggle } from "@/admin/ui";

export default function CollectionManager({ name }) {
  const cfg = COLLECTION_SCHEMAS[name];
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/${name}`).then((r) => setItems(r.data)).catch(() => toast.error("Failed to load items"));
  }, [name]);

  useEffect(load, [load]);

  if (!cfg) return <p className="text-sm text-subtle">Unknown collection.</p>;

  const openNew = () => {
    const blank = {};
    cfg.fields.forEach((f) => (blank[f.key] = f.type === "number" ? 5 : ""));
    if (name === "ugc_models") blank.availability = "Available";
    setDraft(blank);
    setEditing("new");
  };

  const openEdit = (item) => {
    const d = {};
    cfg.fields.forEach((f) => (d[f.key] = item[f.key] ?? (f.type === "number" ? 5 : "")));
    setDraft(d);
    setEditing(item.id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      cfg.fields.forEach((f) => {
        let v = draft[f.key];
        if (f.type === "number") v = Number(v) || 0;
        payload[f.key] = v ?? "";
      });
      if (editing === "new") {
        await api.post(`/admin/${name}`, payload);
        toast.success("Created");
      } else {
        await api.put(`/admin/${name}/${editing}`, payload);
        toast.success("Saved");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[cfg.titleKey]}"? This cannot be undone.`)) return;
    await api.delete(`/admin/${name}/${item.id}`).catch(() => toast.error("Delete failed"));
    toast.success("Deleted");
    load();
  };

  const toggle = async (item) => {
    await api.post(`/admin/${name}/${item.id}/toggle`).catch(() => toast.error("Failed"));
    load();
  };

  const move = async (item, direction) => {
    await api.post(`/admin/${name}/${item.id}/move`, { direction }).catch(() => toast.error("Failed"));
    load();
  };

  const renderField = (f) => {
    const onChange = (e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }));
    const setVal = (v) => setDraft((d) => ({ ...d, [f.key]: v }));
    switch (f.type) {
      case "textarea":
        return <Field key={f.key} label={f.label} hint={f.hint}><TextArea value={draft[f.key]} onChange={onChange} rows={f.rows || 3} testid={`collection-field-${f.key}`} /></Field>;
      case "number":
        return <Field key={f.key} label={f.label} hint={f.hint}><TextInput type="number" value={draft[f.key]} onChange={onChange} testid={`collection-field-${f.key}`} /></Field>;
      case "select":
        return (
          <Field key={f.key} label={f.label} hint={f.hint}>
            <Select value={draft[f.key] || ""} onChange={onChange} options={(f.options || []).map((o) => ({ value: o, label: o }))} testid={`collection-field-${f.key}`} />
          </Field>
        );
      case "image":
        return <Field key={f.key} label={f.label} hint={f.hint}><ImageInput value={draft[f.key]} onChange={setVal} testid={`collection-field-${f.key}`} /></Field>;
      default:
        return <Field key={f.key} label={f.label} hint={f.hint}><TextInput value={draft[f.key]} onChange={onChange} testid={`collection-field-${f.key}`} /></Field>;
    }
  };

  return (
    <div data-testid={`admin-collection-${name}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight" data-testid={`admin-collection-title-${name}`}>{cfg.title}</h1>
          <p className="text-xs text-subtle mt-1.5">{cfg.desc}</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent-bright text-white font-semibold px-5 py-2.5 text-xs transition-all duration-300" data-testid={`admin-collection-add-btn-${name}`}>
          <Plus className="h-4 w-4" /> Add New
        </button>
      </div>

      {!items ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 text-accent-bright animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-sm text-subtle mt-6" data-testid={`admin-collection-empty-${name}`}>
          Nothing here yet — add your first item.
        </div>
      ) : (
        <div className="space-y-3 mt-6">
          {items.map((item, i) => (
            <div key={item.id} className={`glass rounded-2xl p-4 flex items-center gap-4 transition-all ${item.visible === false ? "opacity-50" : ""}`} data-testid={`admin-collection-row-${name}-${i}`}>
              {cfg.imgKey && (
                <div className="h-12 w-12 rounded-lg overflow-hidden border border-white/10 bg-[#131327] shrink-0">
                  {item[cfg.imgKey] ? <img src={mediaUrl(item[cfg.imgKey])} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center font-mono-label text-[8px] text-subtle">N/A</div>}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{item[cfg.titleKey]}</p>
                <p className="text-[11px] text-subtle truncate">{item.tagline || item.category || item.language || item.role || item.metric || item.slug || ""}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => move(item, "up")} disabled={i === 0} className="p-2 rounded-lg text-subtle hover:text-white disabled:opacity-25 hover:bg-white/5 transition-colors" data-testid={`admin-collection-move-up-${name}-${i}`} aria-label="Move up"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(item, "down")} disabled={i === items.length - 1} className="p-2 rounded-lg text-subtle hover:text-white disabled:opacity-25 hover:bg-white/5 transition-colors" data-testid={`admin-collection-move-down-${name}-${i}`} aria-label="Move down"><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => toggle(item)} className="p-2 rounded-lg text-subtle hover:text-white hover:bg-white/5 transition-colors" data-testid={`admin-collection-toggle-${name}-${i}`} aria-label="Toggle visibility">
                  {item.visible === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button onClick={() => openEdit(item)} className="p-2 rounded-lg text-subtle hover:text-white hover:bg-white/5 transition-colors" data-testid={`admin-collection-edit-${name}-${i}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(item)} className="p-2 rounded-lg text-subtle hover:text-rose-400 hover:bg-rose-500/10 transition-colors" data-testid={`admin-collection-delete-${name}-${i}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="glass-strong border-white/10 max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`admin-collection-dialog-${name}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-white text-base">{editing === "new" ? `Add to ${cfg.title}` : `Edit — ${cfg.title}`}</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            {cfg.fields.map(renderField)}
            <div className="flex gap-3 mt-6">
              <button onClick={save} disabled={saving} className="flex-1 rounded-full bg-accent hover:bg-accent-bright disabled:opacity-60 text-white font-semibold py-3 text-sm transition-colors" data-testid={`admin-collection-save-${name}`}>
                {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="rounded-full border border-white/15 text-mist hover:text-white px-6 text-sm font-semibold transition-colors" data-testid={`admin-collection-cancel-${name}`}>
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
